'use client';

import { MatchSide, RetireOption } from '@/components/constants/types';
import { ActionButtons, ScoreInputContainer } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { useScoreCard } from '@/contexts/ScoreCardProvider';
import { getGameNumberValue, calculateRetirementScores } from '@/utils/helpers';

interface ConfirmRetireMatchModalProps {
	setShowModal: (showModal: boolean) => void;
	matchId: string;
	scoreFormat: number;
	gameNumber: string;
	team: MatchSide;
	games?: { max: number; winBy: number }[];
}

/**
 * Modal component for confirming game retirement
 * Displays current scores and allows confirming retirement for a specific game
 */
export const ConfirmRetireMatchModal = ({ setShowModal, matchId, scoreFormat, gameNumber, team, games }: ConfirmRetireMatchModalProps) => {
	const { matchupData, saveRetireScores, updateMatchScores, getMatchScores } = useScoreCard();
	const matchScores = getMatchScores(matchId);

	const retirementMessage = `You are retiring in Game ${getGameNumberValue(gameNumber)}. Please enter your score at the time of retirement.`;
	const retireTeam = team;

	// Get current scores for the game being retired
	const gameIndex = parseInt(gameNumber.slice(1));
	const gameKey = `game${gameIndex}` as keyof typeof matchScores.home;

	// Calculate retirement scores using the helper function
	const { homeScore, awayScore } = calculateRetirementScores(gameNumber, games || [], matchScores, team);

	// Create a map of which games should be locked
	const lockedGames = Array.from({ length: scoreFormat }, (_, i) => `G${i + 1}`).reduce(
		(acc, game) => {
			acc[game] = game !== gameNumber; // Lock all games except the retiring game
			return acc;
		},
		{} as Record<string, boolean>
	);

	const handleRetire = () => {
		// Save the retirement status and scores
		saveRetireScores(matchId, gameNumber, team, 'RET' as RetireOption);

		// Update the scores for both teams
		updateMatchScores(matchId, 'home', {
			[gameKey]: homeScore.toString(),
			[`${gameKey}Status`]: team === 'home' ? 'RET' : ''
		});

		updateMatchScores(matchId, 'away', {
			[gameKey]: awayScore.toString(),
			[`${gameKey}Status`]: team === 'away' ? 'RET' : ''
		});

		setShowModal(false);
	};

	return (
		<ModalWrapper
			overlayClassName="z-10"
			className="max-w-[500px]"
			title="Confirm Retire"
			onClose={() => setShowModal(false)}
			closeOnOutsideClick
		>
			<div className="flex flex-col gap-2 py-4">
				{/* Away Team Section */}
				<p className="text-lg font-medium text-blue-500">
					{matchupData.teamTwoTitle} <span className="text-lg font-medium text-gray-900">(A)</span>
				</p>
				{team === 'away' && <p className="text-sm font-medium">{retirementMessage}</p>}
				<ScoreInputContainer
					scoreFormat={scoreFormat}
					matchId={matchId}
					team="away"
					showDropdown={false}
					games={games}
					retiredGames={[gameNumber]}
					retiredTeams={{ [gameNumber]: retireTeam }}
					retireOptions={{ [gameNumber]: 'RET' }}
					adjustedScores={{ [gameNumber]: { homeScore, awayScore } }}
					lockedGames={lockedGames}
				/>

				{/* Home Team Section */}
				<p className="text-lg font-medium text-blue-500">
					{matchupData.teamOneTitle} <span className="text-lg font-medium text-gray-900">(H)</span>
				</p>
				{team === 'home' && <p className="text-sm font-medium">{retirementMessage}</p>}
				<ScoreInputContainer
					scoreFormat={scoreFormat}
					matchId={matchId}
					team="home"
					showDropdown={false}
					games={games}
					retiredGames={[gameNumber]}
					retiredTeams={{ [gameNumber]: retireTeam }}
					retireOptions={{ [gameNumber]: 'RET' }}
					adjustedScores={{ [gameNumber]: { homeScore, awayScore } }}
					lockedGames={lockedGames}
				/>
			</div>

			{/* Action Buttons */}
			<ActionButtons
				className="flex justify-end gap-3"
				closeButtonClassName="w-32 text-gray-700"
				submitButtonClassName="w-32 border-gray-200 bg-orange-500 text-white hover:bg-orange-700 hover:shadow-sm"
				onClose={() => setShowModal(false)}
				onSubmit={handleRetire}
				submitButtonText="Save"
				closeButtonText="Cancel"
				submitButtonDisabled={false}
			/>
		</ModalWrapper>
	);
};
